import { useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useDemoState } from "@/data/demoState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Package, Eye, Download, FileText, AlertTriangle, CheckCircle2, Calendar,
  Info, ArrowRight, Users
} from "lucide-react";

export default function IncomingPacks() {
  const { packs } = useDemoState();
  const [selected, setSelected] = useState(null);
  const incomingPacks = packs.filter((p) => p.status !== "complete");

  return (
    <AppLayout>
      <div className="space-y-4" data-testid="incoming-packs-page">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="border-blue-100 bg-blue-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-blue-700 mb-0.5">What this page does</p>
                <p className="text-xs text-blue-600">Packs sent by builders for your review. See what is included and what needs attention.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <ArrowRight className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">What happens next</p>
                <p className="text-xs text-slate-500">Open a pack, review evidence against conditions. Accept, request more, or add review notes.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Users className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">Who uses this</p>
                <p className="text-xs text-slate-500">Building surveyor, review team. Collaboration and review layer only.</p>
              </div>
            </CardContent>
          </Card>
        </div>
        <Card className="border-slate-200" data-testid="packs-table">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              Incoming Packs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50/50 border-b border-slate-100">
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Project</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Stage</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Files</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Completeness</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="text-right py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {incomingPacks.map((pack) => (
                    <tr
                      key={pack.id}
                      className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors cursor-pointer ${
                        selected?.id === pack.id ? "bg-blue-50/30" : ""
                      }`}
                      onClick={() => setSelected(pack)}
                      data-testid={`pack-row-${pack.id}`}
                    >
                      <td className="py-3 px-4">
                        <p className="font-medium text-slate-900">{pack.project}</p>
                        <p className="text-xs text-slate-400">Created {pack.createdDate}</p>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className="text-[10px]">{pack.type}</Badge>
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-600">{pack.stage}</td>
                      <td className="py-3 px-4 text-sm text-slate-700">{pack.items} items</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Progress value={pack.completeness} className="h-1.5 w-16" />
                          <span className="text-xs text-slate-500">{pack.completeness}%</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <StatusBadge status={pack.status === "pending-review" ? "pending-review" : pack.status} size="sm" />
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={(e) => { e.stopPropagation(); setSelected(pack); }}>
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2">
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Detail Sheet */}
        <Sheet open={!!selected} onOpenChange={() => setSelected(null)}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="pack-detail-sheet">
            {selected && (
              <>
                <SheetHeader>
                  <SheetTitle className="font-heading text-lg">{selected.type}</SheetTitle>
                  <SheetDescription>
                    {selected.project} - {selected.stage}
                  </SheetDescription>
                </SheetHeader>
                <div className="mt-5 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Files included</p>
                      <p className="text-lg font-heading font-bold text-slate-900">{selected.items}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Completeness</p>
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-heading font-bold text-slate-900">{selected.completeness}%</p>
                        <Progress value={selected.completeness} className="h-1.5 flex-1" />
                      </div>
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Generated by</p>
                    <p className="text-sm text-slate-900">{selected.generatedBy}</p>
                  </div>

                  {/* Mock file list */}
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Pack contents</p>
                    <div className="space-y-1.5">
                      {["Construction Management Plan.pdf", "Sediment Control Photos.zip", "TPZ Fencing Evidence.zip", "Drainage Certificate.pdf", "Soil Report.pdf", "Site Layout Plan.pdf"].map((f, i) => (
                        <div key={i} className="flex items-center gap-2 bg-white border border-slate-100 rounded-lg p-2">
                          <FileText className="w-3.5 h-3.5 text-slate-400" />
                          <span className="text-xs text-slate-700 flex-1">{f}</span>
                          <Eye className="w-3 h-3 text-slate-300 cursor-pointer hover:text-slate-500" />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button size="sm" className="bg-slate-900 text-white hover:bg-slate-800 gap-1.5" onClick={() => window.location.href = "/surveyor/review-centre"}>
                      <Eye className="w-3.5 h-3.5" /> Review this pack
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1.5">
                      <Download className="w-3.5 h-3.5" /> Download all
                    </Button>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </AppLayout>
  );
}
